import { useState } from 'react';

import { Stack, Typography } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { type Value } from '@/james-playground/aa-family-claim/claim/facts';
import { labelOf } from '@/james-playground/aa-family-claim/claim/labels';
import { FALLS } from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  ApproxDate,
  CardShell,
  ChoiceCards,
  Count,
  EntryField,
  Part,
  Parts,
  Toggle,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  approxText,
  list,
  parseApprox,
  str,
  toggleFalls,
  type Approx,
  type FallsPick,
} from '@/james-playground/aa-family-claim/claim/cards/values';

const SIDES = [
  {
    pick: 'Fall',
    heading: FALLS.fallsHeading,
    last: 'lastFall',
    lastQ: FALLS.lastFall,
    month: 'fallsLastMonth',
    year: 'fallsLastYear',
    unit: FALLS.fallsUnit,
  },
  {
    pick: 'Stumble',
    heading: FALLS.stumblesHeading,
    last: 'lastStumble',
    lastQ: FALLS.lastStumble,
    month: 'stumblesLastMonth',
    year: 'stumblesLastYear',
    unit: FALLS.stumblesUnit,
  },
] as const;

/**
 * Falls and stumbles. Falls, stumbles or neither first, then only what the
 * form asks for the answer given: when the last one was, how many in the last
 * month and year, help getting up, a falls clinic, and what happens.
 */
const FallsCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const { draft, set, visible } = useDraft(ask, facts);
  const qid = useQids();
  const [dates, setDates] = useState<Record<string, Approx>>(() => ({
    lastFall: parseApprox(draft.lastFall),
    lastStumble: parseApprox(draft.lastStumble),
  }));
  const gateId = ask.gate ?? 'fallsOrStumbles';
  const picks = list(draft[gateId]);
  // The form's follow-ups are for a fall or a stumble; "Neither" moves on.
  const any = picks.includes('Fall') || picks.includes('Stumble');
  const shown = (id: string) => any && visible(id);
  const sides = SIDES.filter((s) => picks.includes(s.pick));

  const ready =
    picks.length > 0 &&
    (!any ||
      !shown('needsHelpAfterFall') ||
      str(draft.needsHelpAfterFall) !== '');

  const submit = () => {
    const values: Record<string, Value> = { [gateId]: picks };
    if (any) {
      for (const id of ask.entries) {
        if (id === gateId || !shown(id)) continue;
        const value =
          id in dates ? approxText(dates[id]) : str(draft[id]).trim();
        if (value) values[id] = value;
      }
    }
    const reply = !any
      ? FALLS.options[2].label
      : [
          ...sides.map((s) => {
            const last = values[s.last] as string | undefined;
            const year = values[s.year] as string | undefined;
            return [
              s.heading,
              last && `the last in ${last}`,
              year && `${year} in the last year`,
            ]
              .filter(Boolean)
              .join(', ');
          }),
          values.needsHelpAfterFall &&
            `${labelOf('needsHelpAfterFall', v)}: ${values.needsHelpAfterFall}`,
        ]
          .filter(Boolean)
          .join('. ');
    onSubmit(values, reply);
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onContinue={submit}
      onSkip={onSkip}
    >
      <ChoiceCards
        multi
        label={say(ask.prompt, v)}
        options={FALLS.options.map((o) => ({ value: o.value, label: o.label }))}
        selected={picks}
        onPick={(p) => set(gateId, toggleFalls(picks, p as FallsPick))}
      />
      {any && (
        <Parts>
          {sides.map((s) => (
            <Stack
              key={s.pick}
              spacing={2}
              role='group'
              aria-labelledby={qid(s.pick)}
            >
              <Typography id={qid(s.pick)} variant='h6'>
                {s.heading}
              </Typography>
              {shown(s.last) && (
                <Part qid={qid(s.last)} question={s.lastQ}>
                  <ApproxDate
                    labelledBy={qid(s.last)}
                    years={10}
                    value={dates[s.last]}
                    onChange={(a) => setDates((d) => ({ ...d, [s.last]: a }))}
                  />
                </Part>
              )}
              {[s.month, s.year].filter(shown).map((id) => (
                <Part
                  key={id}
                  qid={qid(id)}
                  question={id === s.month ? FALLS.lastMonth : FALLS.lastYear}
                >
                  <Count
                    label={`${s.heading}: ${(id === s.month ? FALLS.lastMonth : FALLS.lastYear).toLowerCase()}`}
                    unit={s.unit}
                    value={str(draft[id])}
                    onChange={(value) => set(id, value)}
                    min={0}
                    max={365}
                  />
                </Part>
              ))}
            </Stack>
          ))}
          {shown('needsHelpAfterFall') && (
            <Stack spacing={1.5}>
              <Toggle
                question={say(FALLS.needsHelp, v)}
                value={str(draft.needsHelpAfterFall)}
                onChange={(value) => set('needsHelpAfterFall', value)}
              />
              {shown('gettingUpAfterFall') && (
                <EntryField
                  id='gettingUpAfterFall'
                  v={v}
                  label={say(FALLS.gettingUp, v)}
                  value={str(draft.gettingUpAfterFall)}
                  onChange={(value) => set('gettingUpAfterFall', value)}
                />
              )}
            </Stack>
          )}
          {shown('fallsClinicReferral') && (
            <Toggle
              question={say(FALLS.clinic, v)}
              value={str(draft.fallsClinicReferral)}
              onChange={(value) => set('fallsClinicReferral', value)}
            />
          )}
          {shown('falls') && (
            <EntryField
              id='falls'
              v={v}
              label={say(FALLS.whatHappens, v)}
              hint={say(FALLS.whatHappensHint, v)}
              value={str(draft.falls)}
              onChange={(value) => set('falls', value)}
            />
          )}
        </Parts>
      )}
    </CardShell>
  );
};

FallsCard.displayName = 'FallsCard';
export default FallsCard;
